<script lang="ts" setup>
  import { Loader2 } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, ref, onBeforeUnmount, watch } from 'vue'
  import Progress from '../components/ui/progress/progress.vue'
  import PdfDisplay from '@/components/pdf/PdfDisplay.vue'
  import PdfNavigation from '@/components/pdf/PdfNavigation.vue'
  import SignModal from '@/components/pdf/SignModal.vue'

  import { downloadDocument } from '@/lib/utils'
  import { usePDF } from '@/store/pdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import Button from '@/components/ui/button/Button.vue'
  import '@tato30/vue-pdf/style.css'
  import CompletedPdf from '@/components/pdf/completedPdf.vue'
  import client404 from '@/components/404/404.vue'
  import metricsService from '@/services/metrics.service'
  import BackIcon from '@/components/icons/backIcon.vue'
  import ClientPortalItemsHeader from '@/components/clientPortal/clientPortalItemsHeader.vue'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { useRoute } from 'vue-router'
  import { useThemeColor } from '@/composables/useThemeColor'
  import { client } from '@hey-api/client-fetch'

  const pdf = usePDF()
  const signatureStore = useSignatures()
  const clientActionsStore = useClientActionStore()
  const themeColor = computed(() => useThemeColor().value)
  const width = ref<number>(window.innerWidth)

  const compactMode = computed(() => {
    return width.value < 768
  })
  const route = useRoute()

  const accessToken = computed(() => route.query.access_token as string | undefined)
  if (accessToken.value) {
    clientActionsStore.setAccessToken(accessToken.value)
  }
  watch(accessToken, (newToken) => {
    if (newToken) {
      clientActionsStore.setAccessToken(newToken)
    }
  })

  const isExternal = (route.query.access_token?.toString() || '') === ''
  onMounted(() => {
    clientActionsStore.fetchOrganisation()
    pdf.doMount()
    window.addEventListener('resize', () => (width.value = window.innerWidth))
    metricsService.trackClientPdfDocument(clientActionsStore?.getClientAction?.org_id!)
  })

  onBeforeUnmount(() => {
    signatureStore.cleanup()
  })

  onUnmounted(() => {
    window.addEventListener('resize', () => (width.value = window.innerWidth))
    pdf.unloadPdf()
    signatureStore.clearSignatures()
  })

  function handleDownloadClick() {
    if (!pdf.selectedForm) {
      return
    }

    downloadDocument(
      pdf.selectedForm.documentProperties.download.url,
      pdf.selectedForm.documentProperties.title
        ? pdf.selectedForm.documentProperties.title + '.pdf'
        : pdf.selectedForm.documentProperties.download.filename
    )
  }
</script>

<template>
  <div>
    <!-- Check if there is a selected form -->
    <div v-if="pdf.selectedForm">
      <!-- Check if the document is signed -->
      <CompletedPdf
        v-if="pdf.selectedForm.documentProperties.tag === 'SIGNED'"
        :handleDownloadClick="handleDownloadClick"
      />

      <!-- Display the following section if the document is not signed -->
      <div
        v-else
        class="mt-0 min-h-screen bg-gray-50 py-4"
      >
        <!-- Show the PDF content only if loading is complete -->
        <div v-if="pdf.loadingProgress >= 100">
          <ClientPortalItemsHeader
            :title="pdf.selectedForm.documentProperties.title"
            usedBy="PDF"
            :is-external="isExternal"
          />
        </div>
        <div
          v-if="pdf.loadingProgress >= 100"
          class="mx-auto mb-6 mt-20 flex w-4/5 max-w-[800px] flex-col justify-center lg:bg-white lg:shadow-lg"
        >
          <PdfDisplay :editable="!pdf.waitingForServerResponse" />
          <SignModal v-if="signatureStore.showSignModal" />
          <div class="m-5 flex justify-between">
            <PdfNavigation :show-prev-next-buttons="false" />
            <div class="mt-0">
              <Button
                class="lg:mr-1"
                :class="`bg-${themeColor}-600 hover:bg-${themeColor}-800`"
                data-cy="clientResponseViewSendButton"
                :disabled="
                  !(
                    pdf.ready &&
                    pdf.hasAllRequired &&
                    !pdf.waitingForServerResponse &&
                    signatureStore.hasAllSignatures
                  )
                "
                @click="pdf.submitForm"
              >
                Dokument abschicken
                <Loader2
                  v-if="pdf.waitingForServerResponse"
                  class="ml-2 mr-2 h-4 w-4 animate-spin"
                />
              </Button>
            </div>
          </div>
        </div>
        <!-- Display loading PDF progress -->
        <div
          v-else
          class="flex h-screen items-center justify-center"
        >
          <Progress
            :model-value="Math.floor(pdf.loadingProgress)"
            class="w-3/5"
          />
        </div>
      </div>
    </div>

    <!-- Display this message if there is no selected form -->
    <div
      v-else
      class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan py-4"
    >
      <div v-if="!pdf.selectedForm && pdf.initDone">
        <client404 />
      </div>
    </div>
    <div id="confirm-modal"></div>
  </div>
</template>

<style scoped>
  input[type='text'],
  textarea {
    border: 1px solid #e5e7eb !important;
    border-radius: 0;
    padding: 0.5rem;
    font-size: 1rem;
    width: 100%;
  }
</style>
